import { useTranslation } from "react-i18next";

const SECTIONS_EN = [
  {
    title: "1. General Provisions. Public Offer",
    body: `This Agreement is the official public offer from the Operator to enter into a service agreement on the terms set out below. This Agreement is public — in accordance with Article 633 of the Civil Code of Ukraine, its terms are identical for all Users regardless of their legal status.

By registering on profioplatform.com or by using any part of the Platform, the User performs full and unconditional acceptance of this Public Offer within the meaning of Article 642 of the Civil Code of Ukraine.

If you do not agree with any provision of this Agreement, please do not use the Platform.

1.1. This document is a public contract (offer) in accordance with Articles 633 and 641 of the Civil Code of Ukraine and the Law of Ukraine "On Electronic Commerce" No. 675-VIII.

1.2. The Agreement is deemed concluded from the moment the User registers an account on profioplatform.com or first uses the Platform's services.

1.3. This Offer is effective as of May 2026 and remains in force until withdrawn or superseded by a new edition.`,
  },
  {
    title: "2. Terms and Definitions",
    body: `2.1. Platform — the Profio website at profioplatform.com and any related mobile or web applications enabling Users to discover Specialists and interact with them.

2.2. Operator — Profio (profioplatform.com), contact: support@profioplatform.com (hereinafter also referred to as "Profio").

2.3. Client — a User who registers on the Platform to search for and request services from Specialists.

2.4. Specialist — a User who registers on the Platform to offer online consultation services (legal, psychological, financial, coaching, IT, and other professional areas) and who acts as an independent contractor.

2.5. Request — a structured inquiry submitted by a Client through the Platform describing the type of service required.

2.6. Booking — a confirmed agreement between a Client and a Specialist to hold a specific session at an agreed time and price, facilitated through the Platform's scheduling tools.

2.7. Wallet — an internal account associated with a Specialist's profile in which Points are held.

2.8. Points — units of account credited to a Specialist's Wallet upon top-up; used to unlock Client contact details or Requests; do not constitute currency or financial instruments.

2.9. Verification — the identity confirmation procedure a Specialist must complete via the Platform's Telegram bot before their profile becomes publicly visible.

2.10. Acceptance — the User's full and unconditional agreement to this Public Offer, effected by registering an account or by using the Platform.`,
  },
  {
    title: "3. Subject of the Agreement",
    body: `3.1. The Platform Operator grants Users access to the Profio online marketplace — a technology platform that facilitates the discovery of and communication between Clients and independent Specialists across multiple service categories.

3.2. Profio provides exclusively an information technology platform and intermediary service. Profio is not a party to any service agreement concluded directly between a Client and a Specialist.

3.3. Profio does not control, direct, supervise, guarantee, or endorse the quality, safety, legality, or accuracy of services offered or delivered by Specialists.

3.4. Specialists are independent contractors. Nothing in this Offer creates an employment, agency, partnership, joint venture, or franchise relationship between Profio and any Specialist.`,
  },
  {
    title: "4. Registration and Account",
    body: `4.1. To access the full functionality of the Platform, a User must register by providing accurate, complete, and up-to-date information.

4.2. The minimum age to register is 16 years. By registering, the User confirms that they meet this requirement.

4.3. Each person may maintain only one account. Creating duplicate or linked accounts to circumvent restrictions is prohibited.

4.4. The User is solely responsible for maintaining the confidentiality of their login credentials and for all activity performed under their account.

4.5. If the User suspects unauthorized access to their account, they must notify Profio immediately at support@profioplatform.com.

4.6. Accounts that contain false information or are used for fraudulent purposes may be suspended or permanently deleted without prior notice.`,
  },
  {
    title: "5. Specialist Account and Verification",
    body: `5.1. A Specialist who wishes to make their profile publicly visible must complete Verification via the Platform's designated Telegram bot before their profile is activated.

5.2. By initiating Verification, the Specialist expressly consents — within the meaning of Article 11 of the Law of Ukraine "On Personal Data Protection" No. 2297-VI — to the collection and processing of their personal data, including a photo or video, for the sole purpose of identity confirmation.

5.3. The Verification procedure is carried out via the Telegram messenger application, operated by Telegram Messenger Inc. / TG Global Limited (Dubai, UAE). By using the Telegram bot, the Specialist acknowledges that their interaction with the bot is governed by Telegram's Terms of Service (https://telegram.org/tos) and Privacy Policy (https://telegram.org/privacy). Profio does not control and is not responsible for Telegram's data processing practices.

5.4. Profio receives and stores verification materials (photo or video) solely for the purpose of identity confirmation and does not share them with third parties except as required by law. Verification materials are retained for 12 months from the date of Verification or from the date of account deletion, whichever is earlier. Upon the Specialist's written request to support@profioplatform.com, verification materials will be deleted within 30 days.

5.5. Profio may, at its discretion, request additional supporting documentation during or after the Verification process.

5.6. The "Verified" status confirms only that identity checks were completed at the time of Verification. It does not constitute an endorsement of the Specialist's qualifications, professional competence, or service quality.

5.7. Verified status may be revoked at any time if Profio determines that the Specialist provided false or misleading information during Verification.

5.8. Each Specialist is an independent contractor who bears full personal responsibility for the quality and lawfulness of the services they provide, as well as for compliance with applicable Ukrainian law, including tax legislation.`,
  },
  {
    title: "6. Submitting and Responding to Requests",
    body: `6.1. A Client submits a Request through the Platform by completing the relevant form and specifying the service required, timing, format, and other material details.

6.2. Specialists who have unlocked a Request using their Points may view the Client's contact details and reach out to the Client directly.

6.3. A Specialist's decision to respond to a Request constitutes an expression of interest; a binding agreement between the Client and the Specialist arises only when both parties confirm the terms of a Booking.

6.4. Acceptance of a Request by a Specialist creates a direct contractual relationship exclusively between that Specialist and the Client. Profio is not a party to that agreement and bears no liability under it.`,
  },
  {
    title: "7. Bookings and Sessions",
    body: `7.1. A Booking is a direct agreement between a Client and a Specialist regarding a specific service session. Profio provides scheduling and communication tools but is not a party to and accepts no liability under any Booking.

7.2. Cancellation terms are set individually by each Specialist and must be communicated to the Client prior to confirming the Booking. Profio is not liable for cancellations, no-shows, or the failure of either party to fulfill their Booking obligations.

7.3. Disputes arising from a specific Booking or session should first be raised directly with the other party. If no resolution is reached, either party may escalate the matter to Profio support at support@profioplatform.com.

7.4. Profio may, at its sole discretion, assist in mediating a resolution but is under no obligation to do so and cannot compel any outcome.`,
  },
  {
    title: "8. Prices and Payments",
    body: `8.1. Payments on the Platform are processed via Monobank or other payment providers available at the time of the transaction.

8.2. Session prices are set independently by each Specialist and are displayed on their profile. Profio does not fix or regulate Specialist pricing.

8.3. Profio may deduct a platform service fee from amounts passing through the Specialist's Wallet in accordance with the fee schedule published on the Platform.

8.4. Prices displayed at the time a Booking is confirmed are locked for that Booking and may not be unilaterally altered by the Specialist after confirmation.

8.5. Profio reserves the right to update the platform fee schedule. Changes take effect as provided in Section 18 of this Offer.`,
  },
  {
    title: "9. Specialist Wallet and Points",
    body: `9.1. Each Specialist profile is associated with a Wallet. Specialists top up their Wallet with real money through the available payment methods; the deposited amount is converted to Points at the rate displayed on the Platform at the time of top-up.

9.2. Unlocking a Client's Request deducts the applicable number of Points from the Specialist's Wallet. The Points cost per Request is displayed before confirmation.

9.3. Points have no monetary equivalent and cannot be exchanged for cash or transferred to another account, except as required by applicable Ukrainian law.

9.4. Wallet balances are generally non-refundable. Refunds may be considered on a case-by-case basis at Profio's discretion only in cases of verified technical errors.

9.5. Profio reserves the right to adjust Wallet balances in cases of abuse, fraud, or verified technical error, with prior notification to the affected User where reasonably practicable.`,
  },
  {
    title: "10. Rights and Obligations of the Parties",
    body: `10.1. Rights of the Client:
(a) to search for and contact Specialists via the Platform;
(b) to receive accurate information about Specialists and their services;
(c) to raise a dispute with Profio support if a direct resolution with the Specialist fails.

10.2. Obligations of the Client:
(a) to provide truthful information when submitting a Request or registering;
(b) to honor confirmed Bookings or cancel in accordance with the Specialist's cancellation policy;
(c) to communicate with Specialists in good faith and in compliance with applicable law.

10.3. Rights of the Specialist:
(a) to set their own prices, availability, and cancellation policy;
(b) to accept or decline any Request at their discretion;
(c) to receive Points corresponding to deposited amounts and to use them to unlock Requests.

10.4. Obligations of the Specialist:
(a) to provide services to a standard consistent with the Specialist's profile description;
(b) to complete Verification before activating their public profile;
(c) to comply with all applicable Ukrainian laws, including tax obligations;
(d) to resolve Client disputes in good faith before escalating to Profio.

10.5. Rights of Profio:
(a) to modify, restrict, or suspend Platform functionality at any time with reasonable notice;
(b) to remove content or suspend accounts that violate this Offer;
(c) to update the fee schedule in accordance with Section 18.

10.6. Obligations of Profio:
(a) to maintain the Platform in operational condition with reasonable efforts;
(b) to process payments securely;
(c) to review and respond to legitimate User complaints within a reasonable timeframe.`,
  },
  {
    title: "11. Prohibited Conduct",
    body: `11.1. Users are strictly prohibited from:
(a) misrepresenting their identity, credentials, qualifications, or professional licenses;
(b) harassing, threatening, abusing, or discriminating against other Users on any ground;
(c) circumventing, bypassing, or attempting to undermine the Platform's payment or booking systems, including by conducting transactions outside the Platform to avoid fees;
(d) posting or transmitting false, misleading, defamatory, obscene, or unlawful content;
(e) scraping, data-mining, reverse-engineering, decompiling, or creating derivative works based on the Platform without prior written consent from Profio;
(f) using the Platform for any purpose that violates Ukrainian law or the rights of third parties, including intellectual property rights;
(g) creating multiple accounts to circumvent suspensions, limitations, or Verification requirements;
(h) posting deliberately false reviews or ratings about other Users.

11.2. Violation of any prohibition listed in clause 11.1 may result in immediate account suspension or permanent deletion, without refund of any Points or Wallet balance, subject to applicable Ukrainian law.`,
  },
  {
    title: "12. Content and Intellectual Property",
    body: `12.1. Users retain all ownership rights to content they upload to the Platform (profile descriptions, photos, course materials, reviews, etc.).

12.2. By uploading content, the User grants Profio a non-exclusive, royalty-free, worldwide license to display, reproduce, and distribute that content solely for the purpose of operating the Platform, for the duration of the User's account.

12.3. Users are solely responsible for ensuring that uploaded content does not infringe the intellectual property rights, privacy rights, or other rights of any third party.

12.4. Profio may remove any content that, in its reasonable assessment, violates this Offer, applicable law, or the rights of third parties, with notice to the User where practicable.

12.5. All trademarks, software, design elements, and other intellectual property forming part of the Profio Platform belong to the Platform Operator or its licensors. Users may not use such materials without prior written consent.`,
  },
  {
    title: "13. Limitation of Liability",
    body: `13.1. The Platform is provided on an "as is" and "as available" basis. Profio does not warrant uninterrupted, error-free, or secure operation.

13.2. To the maximum extent permitted by Ukrainian law, Profio is not liable for:
(a) the quality, safety, legality, professional suitability, or accuracy of services offered or delivered by Specialists;
(b) the accuracy or completeness of Specialist profiles or User-submitted information;
(c) any disputes, losses, harm, or damages arising directly from agreements or transactions between Clients and Specialists;
(d) indirect, incidental, special, punitive, or consequential damages arising from the use of or inability to use the Platform.

13.3. Profio's total aggregate liability to any User for direct damages shall not exceed the total amount paid by that User to Profio in the three (3) calendar months immediately preceding the event giving rise to the claim.

13.4. The limitation in clause 13.3 does not apply to liability arising from Profio's intentional misconduct or gross negligence, to the extent prohibited by Ukrainian law.`,
  },
  {
    title: "14. Tax Obligations of Specialists",
    body: `14.1. Specialists using the Platform act exclusively as independent contractors. Profio does not direct or control how Specialists deliver their services.

14.2. Each Specialist is personally and fully responsible for:
(a) calculating and paying all taxes and mandatory social contributions on income earned through the Platform — whether as an individual entrepreneur (ФОП) under a simplified or general tax regime, or as an individual liable for personal income tax (ПДФО) and military levy under the Tax Code of Ukraine;
(b) registering as a ФОП where required by Ukrainian tax law;
(c) issuing fiscal receipts, acts of services rendered, or other required documents to Clients where required by law.

14.3. Profio does not act as a tax agent for Specialists and does not withhold or remit any taxes on their behalf, except where explicitly required by applicable Ukrainian legislation.`,
  },
  {
    title: "15. Right of Withdrawal",
    body: `15.1. In accordance with the Law of Ukraine "On Consumer Rights Protection" and the applicable rules on distance contracts for digital services, a User who is a consumer has the right to withdraw from an agreement for digital services within 14 calendar days from the date of its conclusion, without stating a reason.

15.2. The right of withdrawal applies only where access to the digital service has not yet been activated or consumed (e.g., where Points have been purchased but not yet used to unlock any Request).

15.3. The right of withdrawal ceases to apply once the User has commenced use of the digital service (Points credited and used), unless otherwise required by law.

15.4. To exercise the right of withdrawal, the User must send a written notice to support@profioplatform.com within the 14-day period specifying: full name, account email, date of purchase, and a statement of intent to withdraw.`,
  },
  {
    title: "16. Personal Data",
    body: `16.1. By accepting this Offer, the User acknowledges that Profio processes their personal data to the extent necessary for the operation of the Platform, in accordance with the Law of Ukraine "On Personal Data Protection" No. 2297-VI.

16.2. The full terms of personal data processing, the purposes and legal bases of processing, third-party data recipients, retention periods, and User rights are set out in the Privacy Policy available at profioplatform.com/privacy.

16.3. Users may exercise their rights under the Law (access, rectification, erasure, objection, portability) by contacting support@profioplatform.com.`,
  },
  {
    title: "17. Force Majeure",
    body: `17.1. Neither Party shall be liable for failure or delay in performance of obligations under this Offer to the extent caused by circumstances beyond that Party's reasonable control, including but not limited to: natural disasters, acts of war, military operations or martial law in Ukraine, government actions, infrastructure failures, or cyberattacks (hereinafter — "Force Majeure").

17.2. The Party invoking Force Majeure must notify the other Party in writing within 5 calendar days of the onset of the Force Majeure event.

17.3. If a Force Majeure event continues for more than 3 consecutive months, either Party may terminate this Offer by providing written notice, without liability to the other Party.`,
  },
  {
    title: "18. Amendments to this Offer",
    body: `18.1. Profio reserves the right to amend the terms of this Public Offer at any time.

18.2. For material changes (changes to fees, User rights, or fundamental obligations), Profio will notify registered Users via email or an in-platform notice at least 7 calendar days before the changes take effect.

18.3. The updated version of the Offer is published at profioplatform.com/tos and takes effect on the date specified in the notice.

18.4. Continued use of the Platform after the effective date of changes constitutes the User's acceptance of the updated Offer. Users who do not accept the changes must cease using the Platform before the effective date.`,
  },
  {
    title: "19. Dispute Resolution",
    body: `19.1. All disputes arising out of or in connection with this Offer shall first be subject to an attempt at good-faith negotiated resolution between the parties.

19.2. If negotiations do not resolve the dispute within 10 calendar days of the first written notice, the aggrieved party shall file a written pre-trial claim with the other party. The recipient must respond within 30 calendar days of receipt.

19.3. If the dispute is not resolved through the pre-trial procedure, it shall be submitted to the competent court of Ukraine at the location of the plaintiff, in accordance with applicable Ukrainian procedural law.

19.4. This Offer is governed by the laws of Ukraine, including the Civil Code of Ukraine (Arts. 633, 641, 642), the Law of Ukraine "On Electronic Commerce" No. 675-VIII, and other applicable Ukrainian legislation.

19.5. If any provision of this Offer is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.`,
  },
  {
    title: "20. Term and Termination",
    body: `20.1. This Offer takes effect for a specific User from the moment of their registration on the Platform or first use, and remains in force until all obligations between the parties are fully performed or the User's account is deleted.

20.2. Profio may suspend or terminate a User's access to the Platform in cases of: violation of this Offer; fraudulent, abusive, or unlawful activity; or extended account inactivity, with reasonable prior notice where possible.

20.3. A User may close their account at any time by submitting a request to support@profioplatform.com. Upon closure, the User's right to access the Platform ceases.

20.4. Termination does not affect rights or obligations that accrued prior to the date of termination.`,
  },
];

const SECTIONS_UK = [
  {
    title: "1. Загальні положення. Публічна оферта",
    body: `Цей Договір є офіційною та публічною пропозицією Оператора укласти договір про надання послуг на умовах, викладених нижче. Відповідно до статті 633 Цивільного кодексу України цей Договір є публічним — його умови є однаковими для всіх Користувачів незалежно від їх правового статусу.

Реєструючись на profioplatform.com або використовуючи будь-яку частину Платформи, Користувач здійснює повний та беззастережний акцепт цієї Публічної оферти у розумінні статті 642 Цивільного кодексу України.

Якщо ви не погоджуєтесь з будь-яким положенням цього Договору — будь ласка, не використовуйте Платформу.

1.1. Цей документ є публічним договором (офертою) відповідно до статей 633 та 641 Цивільного кодексу України та Закону України «Про електронну комерцію» № 675-VIII.

1.2. Договір вважається укладеним з моменту реєстрації Користувача на profioplatform.com або першого використання послуг Платформи.

1.3. Ця Оферта є чинною з травня 2026 р. і залишається в силі до її відкликання або заміни новою редакцією.`,
  },
  {
    title: "2. Визначення термінів",
    body: `2.1. Платформа — вебсайт Profio за адресою profioplatform.com та пов'язані мобільні або вебзастосунки, що дозволяють Користувачам знаходити Спеціалістів та взаємодіяти з ними.

2.2. Оператор — Profio (profioplatform.com), контактна адреса: support@profioplatform.com (далі також — «Profio»).

2.3. Замовник — Користувач, який реєструється на Платформі з метою пошуку Спеціалістів та замовлення їхніх послуг.

2.4. Спеціаліст — Користувач, зареєстрований на Платформі для надання онлайн-консультаційних послуг (юридичних, психологічних, фінансових, коучингових, IT та інших професійних напрямів), що діє як незалежний підрядник.

2.5. Запит — структурне звернення Замовника, розміщене через Платформу, де описуються необхідний вид послуги, часові рамки, формат та інші суттєві деталі.

2.6. Бронювання — підтверджена домовленість між Замовником і Спеціалістом про проведення конкретної сесії в узгоджений час за узгодженою ціною з використанням інструментів планування Платформи.

2.7. Гаманець — внутрішній рахунок у профілі Спеціаліста, на якому зберігаються Бали.

2.8. Бали — облікові одиниці, що зараховуються на Гаманець Спеціаліста після поповнення; використовуються для розблокування контактних даних Замовників або Запитів; не є валютою та не є фінансовими інструментами.

2.9. Верифікація — процедура підтвердження особи Спеціаліста через визначений Telegram-бот Платформи до публікації його профілю.

2.10. Акцепт — повне та беззастережне прийняття Користувачем умов цієї Публічної оферти шляхом реєстрації акаунту або використання Платформи.`,
  },
  {
    title: "3. Предмет договору",
    body: `3.1. Оператор Платформи надає Користувачам доступ до онлайн-маркетплейсу Profio — технологічної платформи, що забезпечує пошук і комунікацію між Замовниками та незалежними Спеціалістами у різних сферах послуг.

3.2. Profio надає виключно інформаційно-технологічний майданчик та посередницькі послуги. Profio не є стороною жодного договору про надання послуг, укладеного безпосередньо між Замовником та Спеціалістом.

3.3. Profio не контролює, не керує, не гарантує та не схвалює якість, безпечність, законність або точність послуг, що пропонуються чи надаються Спеціалістами.

3.4. Спеціалісти є незалежними підрядниками. Жодне положення цієї Оферти не створює трудових, агентських, партнерських або будь-яких інших подібних відносин між Profio та будь-яким Спеціалістом.`,
  },
  {
    title: "4. Реєстрація та обліковий запис",
    body: `4.1. Для отримання доступу до повного функціоналу Платформи Користувач повинен зареєструватись, надавши точну, повну та актуальну інформацію.

4.2. Мінімальний вік для реєстрації — 16 років. Реєструючись, Користувач підтверджує відповідність цій вимозі.

4.3. Кожна особа може мати лише один акаунт. Створення дублюючих або пов'язаних акаунтів з метою обходу обмежень заборонено.

4.4. Користувач несе виключну відповідальність за збереження конфіденційності своїх облікових даних та за всі дії, вчинені під його акаунтом.

4.5. У разі підозри на несанкціонований доступ до акаунту Користувач зобов'язаний негайно повідомити Profio за адресою support@profioplatform.com.

4.6. Акаунти з недостовірними відомостями або ті, що використовуються у шахрайських цілях, можуть бути призупинені або остаточно видалені без попереднього повідомлення.`,
  },
  {
    title: "5. Акаунт спеціаліста та верифікація",
    body: `5.1. Спеціаліст, який бажає зробити свій профіль загальнодоступним, зобов'язаний пройти Верифікацію через визначений Telegram-бот Платформи до активації профілю.

5.2. Розпочинаючи Верифікацію, Спеціаліст надає свою явну згоду — у розумінні статті 11 Закону України «Про захист персональних даних» № 2297-VI — на збір та обробку персональних даних, зокрема фото або відео, виключно з метою підтвердження особи.

5.3. Процедура Верифікації здійснюється через застосунок Telegram, що функціонує під управлінням Telegram Messenger Inc. / TG Global Limited (Дубай, ОАЕ). Використовуючи Telegram-бот, Спеціаліст підтверджує, що його взаємодія з ботом регулюється Умовами використання Telegram (https://telegram.org/tos) та Політикою конфіденційності Telegram (https://telegram.org/privacy). Profio не контролює та не несе відповідальності за практику обробки даних Telegram.

5.4. Profio отримує та зберігає верифікаційні матеріали (фото або відео) виключно з метою підтвердження особи та не передає їх третім особам, крім випадків, передбачених законом. Верифікаційні матеріали зберігаються протягом 12 місяців з дати Верифікації або з дати видалення акаунту (залежно від того, що настане раніше). За письмовим запитом Спеціаліста на support@profioplatform.com верифікаційні матеріали будуть видалені протягом 30 днів.

5.5. Profio може на власний розсуд вимагати додаткові підтвердні документи в процесі або після Верифікації.

5.6. Статус «Верифікований» підтверджує лише факт проходження перевірки особи на момент Верифікації; він не є підтвердженням кваліфікації Спеціаліста, його професійної компетентності або якості послуг.

5.7. Статус верифікації може бути анульований у будь-який час, якщо Profio встановить, що Спеціаліст надав неправдиві або оманливі відомості.

5.8. Кожен Спеціаліст є незалежним підрядником і несе повну особисту відповідальність за якість та законність послуг, що ним надаються, а також за дотримання чинного законодавства України, зокрема податкового.`,
  },
  {
    title: "6. Розміщення запиту та відповідь на нього",
    body: `6.1. Замовник розміщує Запит через Платформу, заповнивши відповідну форму та вказавши необхідний вид послуги, терміни, формат та інші суттєві деталі.

6.2. Спеціалісти, які розблокували Запит за допомогою своїх Балів, отримують контактні дані Замовника та можуть зв'язатись з ним безпосередньо.

6.3. Відповідь Спеціаліста на Запит є виявленням зацікавленості; обов'язкова домовленість між Замовником і Спеціалістом виникає лише після того, як обидві сторони підтвердять умови Бронювання.

6.4. Прийняття Спеціалістом Запиту створює пряме договірне відношення виключно між цим Спеціалістом і Замовником. Profio не є стороною цього договору та не несе відповідальності за його виконання.`,
  },
  {
    title: "7. Бронювання та сесії",
    body: `7.1. Бронювання є прямою домовленістю між Замовником і Спеціалістом щодо конкретної сесії. Profio надає інструменти планування та комунікації, але не є стороною Бронювання та не несе відповідальності за виконання зобов'язань сторін за ним.

7.2. Умови скасування встановлюються кожним Спеціалістом індивідуально та повідомляються Замовнику до підтвердження Бронювання. Profio не несе відповідальності за скасування, неявки або невиконання будь-якою зі сторін зобов'язань за Бронюванням.

7.3. Суперечки, що виникають з конкретного Бронювання або сесії, спочатку вирішуються безпосередньо між сторонами. Якщо врегулювання не досягнуто, будь-яка зі сторін може звернутись до служби підтримки Profio за адресою support@profioplatform.com.

7.4. Profio може на власний розсуд сприяти врегулюванню спору, однак не зобов'язана це робити та не може нав'язати жодного результату.`,
  },
  {
    title: "8. Ціни та оплата",
    body: `8.1. Платежі на Платформі обробляються через Monobank або інші платіжні сервіси, доступні на момент здійснення транзакції.

8.2. Ціни на сесії встановлюються кожним Спеціалістом самостійно та відображаються у його профілі. Profio не фіксує та не регулює ціноутворення Спеціалістів.

8.3. Profio може утримувати комісію платформи з сум, що проходять через Гаманець Спеціаліста, відповідно до тарифного розкладу, опублікованого на Платформі.

8.4. Ціна, відображена на момент підтвердження Бронювання, є фіксованою для цього Бронювання та не може бути односторонньо змінена Спеціалістом після підтвердження.

8.5. Profio залишає за собою право оновлювати тарифний розклад комісій. Зміни набирають чинності відповідно до розділу 18 цієї Оферти.`,
  },
  {
    title: "9. Гаманець та бали спеціаліста",
    body: `9.1. З кожним профілем Спеціаліста пов'язаний Гаманець. Спеціалісти поповнюють Гаманець реальними коштами через доступні способи оплати; внесена сума конвертується у Бали за курсом, відображеним на Платформі на момент поповнення.

9.2. Розблокування Запиту Замовника списує відповідну кількість Балів з Гаманця Спеціаліста. Вартість у Балах відображається до підтвердження.

9.3. Бали не мають грошового еквівалента та не можуть обмінюватись на готівку або переводитись на інший акаунт, крім випадків, передбачених чинним законодавством України.

9.4. Залишок Гаманця, як правило, поверненню не підлягає. Повернення може розглядатись в індивідуальному порядку на розсуд Profio виключно у випадках підтверджених технічних помилок.

9.5. Profio залишає за собою право коригувати баланси Гаманців у випадках зловживань, шахрайства або підтверджених технічних помилок, з попереднім повідомленням відповідного Користувача там, де це є обґрунтовано можливим.`,
  },
  {
    title: "10. Права та обов'язки сторін",
    body: `10.1. Права Замовника:
(а) здійснювати пошук Спеціалістів та зв'язуватися з ними через Платформу;
(б) отримувати достовірну інформацію про Спеціалістів та їхні послуги;
(в) звертатися до служби підтримки Profio, якщо пряме врегулювання спору зі Спеціалістом виявилося неможливим.

10.2. Обов'язки Замовника:
(а) надавати достовірну інформацію при розміщенні Запиту та реєстрації;
(б) дотримуватись підтверджених Бронювань або скасовувати їх відповідно до умов скасування Спеціаліста;
(в) взаємодіяти зі Спеціалістами добросовісно та відповідно до чинного законодавства.

10.3. Права Спеціаліста:
(а) самостійно встановлювати ціни, доступність і умови скасування;
(б) на власний розсуд приймати або відхиляти будь-який Запит;
(в) отримувати Бали, що відповідають внесеним коштам, та використовувати їх для розблокування Запитів.

10.4. Обов'язки Спеціаліста:
(а) надавати послуги на рівні, що відповідає опису у профілі;
(б) пройти Верифікацію до публікації профілю;
(в) дотримуватися всіх застосовних норм законодавства України, зокрема податкового;
(г) добросовісно намагатися врегулювати спори із Замовниками до їх ескалації у Profio.

10.5. Права Profio:
(а) змінювати, обмежувати або призупиняти функціонал Платформи з попереднім повідомленням;
(б) видаляти контент або призупиняти акаунти, що порушують цю Оферту;
(в) оновлювати тарифний розклад комісій відповідно до розділу 18.

10.6. Обов'язки Profio:
(а) підтримувати Платформу у працездатному стані, докладаючи розумних зусиль;
(б) забезпечувати захищену обробку платежів;
(в) розглядати обґрунтовані скарги Користувачів у розумний строк.`,
  },
  {
    title: "11. Заборонені дії",
    body: `11.1. Користувачам суворо забороняється:
(а) вводити в оману щодо своєї особи, облікових даних, кваліфікації або наявних ліцензій;
(б) переслідувати, погрожувати, ображати або дискримінувати інших Користувачів з будь-яких підстав;
(в) обходити, зламувати або намагатися підірвати платіжну систему чи систему бронювань Платформи, зокрема здійснювати транзакції поза Платформою з метою уникнення комісій;
(г) публікувати або передавати неправдивий, оманливий, наклепницький, непристойний або незаконний контент;
(д) здійснювати масове збирання даних, копіювання, реверс-інжиніринг, декомпіляцію або створення похідних творів на основі Платформи без попередньої письмової згоди Profio;
(е) використовувати Платформу з будь-якою метою, що порушує законодавство України або права третіх осіб, зокрема права інтелектуальної власності;
(є) створювати кілька акаунтів з метою обходу блокувань, обмежень або вимог Верифікації;
(ж) розміщувати свідомо неправдиві відгуки або оцінки щодо інших Користувачів.

11.2. Порушення будь-якої із заборон, передбачених п. 11.1, може призвести до негайного призупинення або остаточного видалення акаунту без повернення Балів або залишку Гаманця відповідно до чинного законодавства України.`,
  },
  {
    title: "12. Контент та інтелектуальна власність",
    body: `12.1. Користувачі зберігають усі права власності на контент, який вони завантажують на Платформу (описи профілів, фотографії, навчальні матеріали, відгуки тощо).

12.2. Завантажуючи контент, Користувач надає Profio невиключну, безоплатну, всесвітню ліцензію на відображення, відтворення та розповсюдження такого контенту виключно з метою функціонування Платформи на строк дії акаунту Користувача.

12.3. Користувачі несуть виключну відповідальність за те, що завантажений ними контент не порушує права інтелектуальної власності, права на приватність та інші права будь-яких третіх осіб.

12.4. Profio може видаляти будь-який контент, який, за її обґрунтованою оцінкою, порушує цю Оферту, чинне законодавство або права третіх осіб, з повідомленням Користувача там, де це є можливим.

12.5. Усі торговельні марки, програмне забезпечення, дизайнерські елементи та інша інтелектуальна власність, що становлять Платформу Profio, належать Оператору Платформи або його ліцензіарам. Користувачі не можуть використовувати ці матеріали без попередньої письмової згоди.`,
  },
  {
    title: "13. Обмеження відповідальності",
    body: `13.1. Платформа надається «як є» та «у міру доступності». Profio не гарантує безперебійну, безпомилкову або захищену роботу Платформи.

13.2. У максимально дозволених чинним законодавством України межах Profio не несе відповідальності за:
(а) якість, безпечність, законність, професійну придатність або точність послуг, що пропонуються чи надаються Спеціалістами;
(б) достовірність або повноту профілів Спеціалістів або інформації, наданої Користувачами;
(в) будь-які суперечки, збитки, шкоду або втрати, що виникають безпосередньо з угод або транзакцій між Замовниками та Спеціалістами;
(г) непрямі, випадкові, спеціальні, штрафні або наслідкові збитки, що виникають у зв'язку з використанням або неможливістю використання Платформи.

13.3. Сукупна відповідальність Profio перед будь-яким Користувачем за прямі збитки не може перевищувати загальну суму, сплачену цим Користувачем Profio за три (3) календарних місяці, що безпосередньо передують події, яка стала підставою для позову.

13.4. Обмеження, передбачене п. 13.3, не застосовується до відповідальності, що виникає з умисних дій або грубої недбалості Profio, у тій мірі, в якій це заборонено законодавством України.`,
  },
  {
    title: "14. Податкові зобов'язання спеціалістів",
    body: `14.1. Спеціалісти, які використовують Платформу, діють виключно як незалежні підрядники. Profio не керує та не контролює спосіб надання Спеціалістами своїх послуг.

14.2. Кожен Спеціаліст особисто та в повному обсязі відповідає за:
(а) самостійне обчислення та сплату всіх податків і обов'язкових соціальних внесків з доходів, отриманих через Платформу, — чи то як фізична особа-підприємець (ФОП) на спрощеній або загальній системі оподаткування, чи то як фізична особа, що сплачує ПДФО та військовий збір відповідно до Податкового кодексу України;
(б) реєстрацію в якості ФОП, якщо цього вимагає чинне податкове законодавство України;
(в) видачу фіскальних чеків, актів наданих послуг або інших необхідних документів Замовникам у випадках, передбачених законодавством.

14.3. Profio не є податковим агентом Спеціалістів і не утримує та не перераховує за них жодних податків, крім випадків, прямо передбачених чинним законодавством України.`,
  },
  {
    title: "15. Право на відмову",
    body: `15.1. Відповідно до Закону України «Про захист прав споживачів» та застосовних норм щодо дистанційних договорів про цифрові послуги, Користувач-споживач має право відмовитися від договору про цифрові послуги протягом 14 календарних днів з дати його укладення, не зазначаючи причини.

15.2. Право на відмову застосовується лише у разі, якщо доступ до цифрової послуги ще не було активовано або використано (наприклад, якщо Бали придбано, але жодного Запиту ще не розблоковано).

15.3. Право на відмову припиняється після того, як Користувач розпочав користуватись цифровою послугою (Бали зараховані та використані), якщо інше не передбачено законодавством.

15.4. Для скористання правом на відмову Користувач зобов'язаний направити письмове повідомлення на support@profioplatform.com протягом 14-денного строку із зазначенням: повного імені, e-mail акаунту, дати купівлі та заяви про намір відмовитись від договору.`,
  },
  {
    title: "16. Персональні дані",
    body: `16.1. Приймаючи цю Оферту, Користувач підтверджує, що Profio обробляє його персональні дані в обсязі, необхідному для функціонування Платформи, відповідно до Закону України «Про захист персональних даних» № 2297-VI.

16.2. Повні умови обробки персональних даних, мета та підстави обробки, одержувачі даних, строки зберігання та права Користувачів викладені в Політиці конфіденційності, доступній за адресою profioplatform.com/privacy.

16.3. Для реалізації прав, передбачених Законом (доступ, виправлення, видалення, заперечення, переносимість), Користувач може звернутися за адресою support@profioplatform.com.`,
  },
  {
    title: "17. Форс-мажор",
    body: `17.1. Жодна зі сторін не несе відповідальності за невиконання або прострочення виконання зобов'язань за цією Офертою, якщо таке невиконання чи прострочення спричинені обставинами непереборної сили, що знаходяться поза розумним контролем сторони, зокрема: стихійні лиха, воєнні дії, збройні конфлікти, введення воєнного стану в Україні, дії органів державної влади, відмови інфраструктури або кібератаки (далі — «форс-мажор»).

17.2. Сторона, що посилається на форс-мажор, зобов'язана письмово повідомити іншу сторону протягом 5 календарних днів з моменту настання такої обставини.

17.3. Якщо форс-мажорна обставина тривалістю понад 3 послідовних місяці, будь-яка зі сторін має право розірвати цю Оферту, направивши письмове повідомлення, без відповідальності перед іншою стороною.`,
  },
  {
    title: "18. Зміни до договору",
    body: `18.1. Profio залишає за собою право вносити зміни до умов цієї Публічної оферти в будь-який час.

18.2. Про суттєві зміни (зміни комісій, прав Користувачів або основних зобов'язань) Profio повідомляє зареєстрованих Користувачів електронною поштою або через повідомлення на Платформі не пізніше ніж за 7 календарних днів до набрання змінами чинності.

18.3. Оновлена редакція Оферти публікується за адресою profioplatform.com/tos та набирає чинності з дати, вказаної у повідомленні.

18.4. Продовження використання Платформи після набрання змінами чинності є прийняттям Користувачем оновленої Оферти. Користувачі, які не погоджуються зі змінами, зобов'язані припинити використання Платформи до дати набрання змін чинності.`,
  },
  {
    title: "19. Вирішення спорів",
    body: `19.1. Усі спори, що виникають з цієї Оферти або у зв'язку з нею, підлягають першочерговому врегулюванню шляхом добросовісних переговорів між сторонами.

19.2. Якщо переговори не привели до врегулювання спору протягом 10 календарних днів з моменту першого письмового звернення, сторона, права якої порушені, направляє іншій стороні письмову претензію. Сторона-одержувач зобов'язана надати відповідь протягом 30 календарних днів з моменту отримання претензії.

19.3. Якщо спір не врегульовано в досудовому порядку, він передається на розгляд компетентного суду України за місцезнаходженням позивача відповідно до чинного процесуального законодавства України.

19.4. Ця Оферта регулюється законодавством України, зокрема Цивільним кодексом України (ст. 633, 641, 642), Законом України «Про електронну комерцію» № 675-VIII та іншим застосовним законодавством України.

19.5. Якщо будь-яке положення цієї Оферти буде визнано недійсним або таким, що не підлягає виконанню, решта положень залишаються в повній силі та дії.`,
  },
  {
    title: "20. Строк дії договору",
    body: `20.1. Ця Оферта набирає чинності для конкретного Користувача з моменту реєстрації на Платформі або першого її використання та залишається в силі до повного виконання всіх зобов'язань між сторонами або видалення акаунту Користувача.

20.2. Profio може призупинити або припинити доступ Користувача до Платформи у разі: порушення цієї Оферти; шахрайської, образливої або незаконної діяльності; тривалої неактивності акаунту — з попереднім повідомленням там, де це є обґрунтовано можливим.

20.3. Користувач може в будь-який час закрити свій акаунт, надіславши відповідний запит на support@profioplatform.com. Після закриття право Користувача на доступ до Платформи припиняється.

20.4. Припинення дії договору не впливає на права та зобов'язання, що виникли до дати його припинення.`,
  },
];

export default function TermsOfService() {
  const { i18n } = useTranslation();
  const isUk = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase().startsWith("uk");

  const sections = isUk ? SECTIONS_UK : SECTIONS_EN;
  const title = isUk ? "Публічна оферта" : "Public Offer (Terms of Service)";
  const updated = isUk ? "Редакція: травень 2026 р." : "Edition: May 2026";
  const intro = isUk
    ? "Реєструючись або користуючись платформою Profio, ви приймаєте умови цієї Публічної оферти відповідно до ст. 642 Цивільного кодексу України. Будь ласка, уважно ознайомтеся з документом."
    : "By registering or using the Profio platform, you accept the terms of this Public Offer in accordance with Article 642 of the Civil Code of Ukraine. Please read this document carefully.";

  return (
    <div className="min-h-screen bg-[#272c39] text-white">
      <div className="mx-auto max-w-3xl px-4 md:px-6 pt-16 pb-28 md:pb-16">
        <h1 className="font-unbounded text-3xl font-bold">{title}</h1>
        <p className="mt-2 text-sm text-white/50">{updated}</p>
        <p className="mt-6 text-white/70 leading-relaxed">{intro}</p>

        <div className="mt-10 space-y-8">
          {sections.map((s) => (
            <section key={s.title}>
              <h2 className="font-semibold text-white text-base mb-2">{s.title}</h2>
              <p className="text-white/70 leading-relaxed text-sm whitespace-pre-line">{s.body}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
